import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
  styled,
  Button,
} from "@mui/material";

import {
  getAllLibraryBooks,
  getLibraryBookSummary,
  addLibraryBook,
  updateLibraryBook,
  deleteLibraryBook,
} from "app/services/schoolReportingServices/library.service";

import CustomLoader from "app/components/common/CustomLoader";
import { Add } from "@mui/icons-material";
import TableActionButtons from "app/components/common/TableActionButtons";
import DeleteConfirmationDialog from "app/components/common/Dialogs/DeleteConfirmationDialog";
import AddBookDialog from "app/components/common/Dialogs/library/AddBookDialog";
import IssueBookDialog from "app/components/common/Dialogs/library/IssueBookDialog";
import { useSelector } from "react-redux";
import { selectActiveSession } from "../schools/selectors";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "react-query";
import ViewDrawer from "./components/ViewDrawer";
import FilterLibraryDialog from "./components/FilterLibraryDialog";
import ExportButtonGroup from "app/components/common/ExportButtonGroup";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExportDialog from "app/components/common/exportDialog";
import PdfSummaryPreviewDialog from "app/components/common/Dialogs/PdfSummaryPreviewDialog";
import CommonTablePagination from "app/components/Tables/CommonTablePagination";

const LIBRARY_HEADER = [
  "Sr.No",
  "Title",
  "Author",
  "Stock",
  "Issued",
  "Balance",
];

const Library = () => {
  const selectedSession = useSelector(selectActiveSession);
  const [state, setState] = useState({
    addDialog: false,
    edit: false,
    editData: {},
    deleteDialog: false,
    deleteDocId: "",
    checkExportOptions: false,
    checkExportOptionsPdf: false,
  });
  const [issueBookDialog, setIssueBookDialog] = useState(false);
  const [studentDrawer, setStudentDrawer] = useState({
    showViewDrawer: false,
    viewData: {},
  });
  const [filterParams, setFilterParams] = useState({});
  const [selectedOptions, setSelectedOptions] = useState(LIBRARY_HEADER);
  const [selectedOptionsForPdf, setSelectedOptionsForPdf] =
    useState(LIBRARY_HEADER);
  const [downloadPdf, setDownloadPdf] = useState(false);

  // --- Mutations ---
  const {
    mutate: deleteLibraryBookMutate,
    isLoading: deleteLibraryBookLoading,
  } = useMutation({
    mutationKey: ["deleteLibraryBook"],
    mutationFn: deleteLibraryBook,
    onSuccess: (success) => {
      handleCloseDelete();
      toast.success(success?.data.message);
      getAllLibraryBooksRefetch();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    },
  });

  const { mutate: addLibraryBookMutate, isLoading: addLibraryBookLoading } =
    useMutation({
      mutationKey: ["addLibraryBook"],
      mutationFn: addLibraryBook,
      onSuccess: (success) => {
        setState((prev) => ({
          ...prev,
          addDialog: false,
          editData: {},
          edit: false,
        }));
        toast.success(success?.data.message);
        getAllLibraryBooksRefetch();
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message);
      },
    });

  const {
    mutate: updateLibraryBookMutate,
    isLoading: updateLibraryBookLoading,
  } = useMutation({
    mutationKey: ["updateLibraryBook"],
    mutationFn: updateLibraryBook,
    onSuccess: (success) => {
      setState((prev) => ({
        ...prev,
        addDialog: false,
        editData: {},
        edit: false,
      }));
      toast.success(success?.data.message);
      getAllLibraryBooksRefetch();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    },
  });

  // --- Queries ---
  const {
    data: getAllLibraryBooksData,
    isLoading: getAllLibraryBooksLoading,
    isFetching: getAllLibraryBooksFetching,
    refetch: getAllLibraryBooksRefetch,
  } = useQuery({
    queryKey: ["getAllLibraryBooks", selectedSession, filterParams],
    queryFn: () =>
      getAllLibraryBooks({ session: selectedSession, ...filterParams }),
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    },
  });

  const {
    data: getLibraryBookSummaryData,
    isLoading: getLibraryBookSummaryLoading,
    isFetching: getLibraryBookSummaryFetching,
    refetch: getLibraryBookSummaryRefetch,
  } = useQuery({
    queryKey: ["getLibraryBookSummary", selectedSession],
    queryFn: () => getLibraryBookSummary({ session: selectedSession }),
    enabled: !!selectedSession,
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "Failed to fetch summary data"
      );
    },
  });

  // --- Handlers ---
  const handleViewAction = (data) => {
    setState((prev) => ({
      ...prev,
      addDialog: true,
      edit: true,
      editData: data,
    }));
  };
  const handleDeleteConfirmation = () => {
    deleteLibraryBookMutate(state.deleteDocId);
  };
  const handleCloseDelete = () => {
    setState((prev) => ({ ...prev, deleteDialog: false, deleteDocId: "" }));
  };

  // --- Table Columns ---
  const columns = useMemo(
    () => [
      {
        header: "Sr.No",
        Cell: ({ cell }) => <Typography>{Number(cell.row.id) + 1}</Typography>,
        maxSize: 30,
      },
      {
        header: "Title",
        accessorKey: "title",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          const imageUrl = original?.image;
          return (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <img
                src={imageUrl || "/image-placeholder.jpeg"}
                alt="book"
                style={{
                  width: "35px",
                  height: "50px",
                  borderRadius: "10%",
                  objectFit: "cover",
                }}
              />
              <Typography sx={{ ml: 1, fontWeight: 600 }}>
                {original?.title || "N/A"}
              </Typography>
            </Box>
          );
        },
        maxSize: 60,
      },
      {
        header: "Author",
        accessorKey: "author",
        maxSize: 60,
      },
      {
        header: "Stock",
        accessorKey: "totalQuantity",
        maxSize: 30,
      },
      {
        header: "Issued",
        accessorKey: "issuedQuantity",
        maxSize: 30,
      },
      {
        header: "Balance",
        Cell: ({ cell }) => {
          const totalQuantity = cell?.row?.original?.totalQuantity ?? 0;
          const issuedQuantity = cell?.row?.original?.issuedQuantity ?? 0;
          const balance = totalQuantity - issuedQuantity;
          return <Typography>{balance < 0 ? 0 : balance}</Typography>;
        },
        maxSize: 30,
      },
      {
        header: "Actions",
        Cell: ({ cell }) => (
          <TableActionButtons
            showView
            showEdit
            showDelete
            handleDelete={() =>
              setState((prev) => ({
                ...prev,
                deleteDialog: true,
                deleteDocId: cell?.row?.original?._id,
              }))
            }
            handleEdit={() => handleViewAction(cell?.row?.original)}
            handleView={() =>
              setStudentDrawer((prev) => ({
                ...prev,
                showViewDrawer: true,
                viewData: cell?.row?.original,
              }))
            }
          />
        ),
        maxSize: 50,
      },
    ],
    []
  );

  // --- Export Data ---
  const exportArray = (getAllLibraryBooksData?.data?.list || []).map(
    (item, idx) => ({
      "Sr.No": idx + 1,
      Title: item?.title || "",
      Author: item?.author || "",
      Stock: item?.totalQuantity ?? 0,
      Issued: item?.issuedQuantity ?? 0,
      Balance: Math.max(
        (item?.totalQuantity ?? 0) - (item?.issuedQuantity ?? 0),
        0
      ),
    })
  );

  // Excel Export Handler
  const handleExport = () => {
    const filteredExportArray = exportArray.map((item) => {
      const filteredItem = {};
      selectedOptions.forEach((option) => {
        filteredItem[option] = item[option];
      });
      return filteredItem;
    });
    const worksheet = XLSX.utils.json_to_sheet(filteredExportArray);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "LibraryBooks");
    XLSX.writeFile(workbook, "library_books.xlsx");
  };

  // PDF Export Handler
  const handleExportPdf = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const rows = exportArray.map((item) =>
      selectedOptionsForPdf.map((option) => item[option])
    );
    const tableColumn = selectedOptionsForPdf.map((col) => ({
      title: col,
      dataKey: col,
    }));
    autoTable(doc, {
      head: [selectedOptionsForPdf],
      body: rows,
      startY: 20,
      headStyles: { fillColor: [39, 137, 253] },
      bodyStyles: { fontSize: 10 },
      theme: "grid",
    });
    doc.save("library_books.pdf");
    setDownloadPdf(false);
  };

  return (
    <>
      <CustomLoader
        show={
          getAllLibraryBooksLoading ||
          getAllLibraryBooksFetching ||
          addLibraryBookLoading ||
          updateLibraryBookLoading ||
          deleteLibraryBookLoading
        }
      />

      <DeleteConfirmationDialog
        open={state.deleteDialog}
        onClose={handleCloseDelete}
        handleDelete={handleDeleteConfirmation}
      />

      <AddBookDialog
        open={state.addDialog}
        loading={addLibraryBookLoading}
        onClose={() =>
          setState((prev) => ({
            ...prev,
            addDialog: false,
            editData: {},
            edit: false,
          }))
        }
        refetch={() => getAllLibraryBooksRefetch()}
        edit={state.edit}
        editData={state.editData}
        addBookMutate={addLibraryBookMutate}
        updateBookMutate={updateLibraryBookMutate}
      />

      <ViewDrawer
        open={studentDrawer.showViewDrawer}
        onClose={() =>
          setStudentDrawer((prev) => ({
            ...prev,
            showViewDrawer: false,
            viewData: {},
          }))
        }
        viewData={studentDrawer.viewData}
        getAllLibraryBooksRefetch={getAllLibraryBooksRefetch}
        getLibraryBookSummaryRefetch={getLibraryBookSummaryRefetch}
      />

      <IssueBookDialog
        open={issueBookDialog}
        onClose={() => setIssueBookDialog(false)}
        refetch={() => {
          getAllLibraryBooksRefetch();
          getLibraryBookSummaryRefetch();
        }}
      />

      <Helmet>
        <title>Library</title>
      </Helmet>
      <Container maxWidth="xl">
        <Box mb={2}>
          <FilterLibraryDialog mutate={setFilterParams} />
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={"space-between"}
        >
          {/* Library Book Summary */}
          <SummaryBox>
            {getLibraryBookSummaryLoading || getLibraryBookSummaryFetching ? (
              <Typography>Loading summary...</Typography>
            ) : getLibraryBookSummaryData ? (
              <Stack direction="row" alignItems={"center"} gap={2}>
                <Box sx={{ mr: "1rem" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 400 }}>
                    Total Different Book
                  </Typography>
                  <Typography style={{ fontWeight: 600, fontSize: "15px" }}>
                    {getLibraryBookSummaryData?.data?.totalDifferentBooks || 0}
                  </Typography>
                </Box>
                <Box sx={{ mr: "1rem" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 400 }}>
                    Total Book
                  </Typography>
                  <Typography style={{ fontWeight: 600, fontSize: "15px" }}>
                    {getLibraryBookSummaryData?.data?.totalBookCount || 0}
                  </Typography>
                </Box>
                <Box sx={{ mr: "1rem" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 400 }}>
                    Available Book
                  </Typography>
                  <Typography style={{ fontWeight: 600, fontSize: "15px" }}>
                    {getLibraryBookSummaryData?.data?.availableBookCount || 0}
                  </Typography>
                </Box>
                <Box sx={{ mr: "1rem" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 400 }}>
                    Issued Book
                  </Typography>
                  <Typography style={{ fontWeight: 600, fontSize: "15px" }}>
                    {getLibraryBookSummaryData?.data?.issuedBookCount || 0}
                  </Typography>
                </Box>
                {/* <Box sx={{ mr: "1rem" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 400 }}>
                    Damaged Book 
                  </Typography>
                  <Typography style={{ fontWeight: 600, fontSize: "15px" }}>
                    {getLibraryBookSummaryData?.data?.damagedBookCount || 0}
                  </Typography>
                </Box>
                <Box sx={{ mr: "1rem" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 400 }}>
                    Lost Book 
                  </Typography>
                  <Typography style={{ fontWeight: 600, fontSize: "15px" }}>
                    {getLibraryBookSummaryData?.data?.lostBookCount || 0}
                  </Typography>
                </Box> */}
                
                <Box sx={{ mr: "1rem" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 400 }}>
                    Overdue Book
                  </Typography>
                  <Typography style={{ fontWeight: 600, fontSize: "15px" }}>
                    {getLibraryBookSummaryData?.data?.overDueBookCount || 0}
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Typography>Failed to load summary data</Typography>
            )}
          </SummaryBox>

          {/* Add and Issue Book */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<Add />}
              size={"large"}
              sx={{
                textTransform: "capitalize",
                fontSize: "14px",
                backgroundColor: "#fff",
                borderRadius: "10px",
              }}
              onClick={() =>
                setState((prev) => ({
                  ...prev,
                  addDialog: true,
                  edit: false,
                  editData: {},
                }))
              }
            >
              Add New
            </Button>

            <Button
              variant="contained"
              color="secondary"
              size={"large"}
              sx={{
                textTransform: "capitalize",
                marginLeft: "10px",
                fontSize: "14px",
                borderRadius: "10px",
              }}
              onClick={() => setIssueBookDialog(true)}
            >
              Issue Book
            </Button>
          </Grid>

          {/* export */}
          <ExportButtonGroup
            showExcel
            showPdf={true}
            handleClickExcel={() =>
              setState((prev) => ({
                ...prev,
                checkExportOptions: true,
              }))
            }
            handleClickPdf={() => {
              setState((prev) => ({
                ...prev,
                checkExportOptionsPdf: true,
              }));
            }}
          />
        </Stack>

        <Box mt={2}>
          <CommonTablePagination
            columns={columns}
            data={getAllLibraryBooksData?.data?.list || []}
            maxHeight="60vh"
            setPagination={getAllLibraryBooksRefetch}
            page={getAllLibraryBooksData?.data?.page || 0}
            totalPage={getAllLibraryBooksData?.data?.totalPage || 0}
          />
        </Box>

        <ExportDialog
          open={state.checkExportOptions}
          handleClose={() =>
            setState((prev) => ({ ...prev, checkExportOptions: false }))
          }
          header={LIBRARY_HEADER}
          exportArray={exportArray}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          handleExport={handleExport}
          fileName="library_books"
        />
        <PdfSummaryPreviewDialog
          open={state.checkExportOptionsPdf}
          handleClose={() =>
            setState((prev) => ({ ...prev, checkExportOptionsPdf: false }))
          }
          data={LIBRARY_HEADER}
          setSelectedOptionsForPdf={setSelectedOptionsForPdf}
          selectedOptionsForPdf={selectedOptionsForPdf}
          handleExportPdf={() => setDownloadPdf(true)}
        />
      </Container>
      {downloadPdf && handleExportPdf()}
    </>
  );
};

export default Library;

const SummaryBox = styled(Box)`
  border-radius: 10px;
  border: 1px solid rgba(19, 115, 229, 0.2);
  background: rgba(39, 137, 253, 0.02);
  padding: 10px;
  display: inline-block;
  margin-top: 10px;
`;
